import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { track } from "@/lib/pixel";
import { syncFromUser } from "@/lib/consent";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("pf_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      syncFromUser(data);
    } catch {
      localStorage.removeItem("pf_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const processGoogle = useCallback(async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google-session", {}, { headers: { "X-Session-ID": sessionId } });
      localStorage.setItem("pf_token", data.token);
      setUser(data);
      syncFromUser(data);
    } catch {
      localStorage.removeItem("pf_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Public Editorial handoff landing (/ez): ignore ANY existing (possibly stale) session.
    // The handoff owns the session — don't run refresh() with an old token, which could race and
    // either overwrite the handoff user or, on a stale/invalid token, wipe the freshly-adopted token.
    if ((window.location.pathname || "") === "/ez") {
      setUser(false);
      setLoading(false);
      return;
    }
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      if (sid) { processGoogle(sid); return; }
    }
    refresh();
  }, [refresh, processGoogle]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("pf_token", data.token);
    setUser(data);
    return data;
  };

  const adminLogin = async (email, password) => {
    const { data } = await api.post("/auth/admin-login", { email, password });
    if (data.otp_required) return data;
    localStorage.setItem("pf_token", data.token);
    setUser(data);
    return data;
  };

  const adminVerifyOtp = async (challenge_id, code) => {
    const { data } = await api.post("/auth/admin-login/verify", { challenge_id, code });
    localStorage.setItem("pf_token", data.token);
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("pf_token", data.token);
    setUser(data);
    track("CompleteRegistration", { content_name: payload?.role || "student" }, data?._id);
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("pf_token");
    setUser(false);
  };

  const updateUser = (patch) => setUser((u) => ({ ...u, ...patch }));

  // Adopt ONLY the verified handoff user's session: replace any stale token/user outright.
  const adoptSession = useCallback((data) => {
    localStorage.setItem("pf_token", data.token);
    setUser(data);
    setLoading(false);
    try { syncFromUser(data); } catch {}
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, adminLogin, adminVerifyOtp, register, logout, refresh, updateUser, adoptSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
